import React, { useMemo } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { ReceptionEmptyState, ReceptionLoadingBlock, ReceptionStatusBadge } from '../../receptionist/ui';
import {
  classifyNursingQueueItem,
  getQueueBucketLabel,
  isActiveNursingQueueItem
} from '../utils/nurseQueueFilters';
import { isConsultationToday } from '../utils/nurseQueueDates';
import { NURSE_COPY } from '../nurseCopy';
import ClinicalButton from '../../doctor/ui/ClinicalButton';

const W = NURSE_COPY.DASHBOARD.WAITING;

function formatTime(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function olderActiveMessage(count) {
  if (count === 1) return W.OLDER_ACTIVE_ONE;
  return W.OLDER_ACTIVE_MANY.replace('{n}', String(count));
}

export default function NurseWaitingListPanel({
  consultations = [],
  loading = false,
  siteName = '',
  onRefresh,
  onOpenConsultation,
  onSeeAllFile,
  todayOnly = true
}) {
  const activeRows = useMemo(
    () =>
      [...consultations]
        .filter((c) => {
          if (!isActiveNursingQueueItem(c)) return false;
          if (todayOnly && !isConsultationToday(c)) return false;
          return true;
        })
        .sort((a, b) => new Date(a.consultation_date) - new Date(b.consultation_date)),
    [consultations, todayOnly]
  );

  const olderActiveCount = useMemo(() => {
    if (!todayOnly) return 0;
    return consultations.filter(
      (c) => isActiveNursingQueueItem(c) && !isConsultationToday(c)
    ).length;
  }, [consultations, todayOnly]);

  const count = activeRows.length;
  const showOlderBanner = todayOnly && olderActiveCount > 0 && !loading;

  return (
    <Card className="border-0 recv-dash-card h-100 recv-dash-card--elevated recv-dash-card--primary recv-queue-card nr-waiting-card">
      <Card.Header className="recv-dash-card__head recv-queue-card__head border-0 bg-transparent mb-0">
        <div>
          <span className="recv-dash-card__eyebrow">{W.EYEBROW}</span>
          <Card.Title className="h5 mb-0 fw-semibold mt-2 text-body">{W.TITLE}</Card.Title>
          <Card.Subtitle className="text-muted small mt-2 mb-0 fw-normal">
            {todayOnly ? W.SUBTITLE_TODAY : W.SUBTITLE}
            {siteName ? <span className="recv-queue-card__site"> · {siteName}</span> : null}
          </Card.Subtitle>
        </div>
        <div className="recv-queue-card__toolbar">
          <span className="recv-queue-card__ok">
            {count} patient{count > 1 ? 's' : ''}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            className="recv-queue-card__refresh"
            onClick={onRefresh}
            disabled={loading}
            aria-label={W.REFRESH_ARIA}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <Icon name="refresh" size={14} aria-hidden />
                {W.REFRESH}
              </>
            )}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {showOlderBanner ? (
          <div className="px-3 pt-3">
            <Alert variant="warning" className="mb-0 nr-waiting-older-banner">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="small mb-0">{olderActiveMessage(olderActiveCount)}</span>
                {onSeeAllFile ? (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 nr-waiting-older-banner__link"
                    onClick={onSeeAllFile}
                  >
                    {W.OLDER_ACTIVE_LINK}
                  </button>
                ) : null}
              </div>
            </Alert>
          </div>
        ) : null}

        {loading ? (
          <ReceptionLoadingBlock label={W.LOADING} />
        ) : activeRows.length === 0 ? (
          <div className="px-3 py-2">
            <ReceptionEmptyState
              title={W.EMPTY_TITLE}
              description={todayOnly ? W.EMPTY_DESC_TODAY : W.EMPTY_DESC}
            />
          </div>
        ) : (
          <>
            <div className="recv-queue-list-wrap">
              <div className="recv-queue-list" role="list" aria-label={W.LIST_ARIA}>
                <div className="recv-queue-list__head" aria-hidden="true">
                  <span className="recv-queue-list__cell recv-queue-list__cell--patient">{W.COL_PATIENT}</span>
                  <span className="recv-queue-list__cell recv-queue-list__cell--line">{W.COL_STATUS}</span>
                  <span className="recv-queue-list__cell recv-queue-list__cell--consult">{W.COL_ACTION}</span>
                </div>
                {activeRows.map((c) => {
                  const bucket = classifyNursingQueueItem(c);
                  const patientName = c.patient_name || 'Patient';
                  const patientMeta = [c.member_matricule, c.member_company_name].filter(Boolean).join(' · ');

                  return (
                    <article key={c.id} className="recv-queue-row nr-waiting-row" role="listitem">
                      <div className="recv-queue-row__patient recv-queue-list__cell recv-queue-list__cell--patient">
                        <span className="recv-queue-row__time font-monospace">
                          {formatTime(c.consultation_date)}
                        </span>
                        <div className="recv-queue-row__name" title={patientName}>
                          {patientName}
                        </div>
                        {patientMeta ? (
                          <div className="recv-queue-row__meta" title={patientMeta}>
                            {patientMeta}
                          </div>
                        ) : null}
                      </div>
                      <div className="recv-queue-row__line recv-queue-list__cell recv-queue-list__cell--line">
                        <span className="recv-service-line recv-service-line--compact recv-service-line--nursing">
                          {getQueueBucketLabel(bucket)}
                        </span>
                        {c.status ? (
                          <ReceptionStatusBadge kind="consultation" value={c.status} />
                        ) : null}
                      </div>
                      <div className="recv-queue-row__consult recv-queue-list__cell recv-queue-list__cell--consult">
                        <ClinicalButton variant="primary" compact onClick={() => onOpenConsultation(c.id)}>
                          {W.ACTION_OPEN}
                        </ClinicalButton>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
            <div className="recv-queue-table__foot d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>
                {count} patient{count > 1 ? 's' : ''}
                {todayOnly ? ' aujourd’hui' : ' en file active'}
                {siteName ? ` · ${siteName}` : ''}
              </span>
              {onSeeAllFile ? (
                <button type="button" className="btn btn-link btn-sm p-0 nr-waiting-see-all" onClick={onSeeAllFile}>
                  {W.SEE_ALL_FILE}
                </button>
              ) : null}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
